import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { useToast } from "../../context/ToastContext";

const ReturnRequisitionForm = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { showToast } = useToast();
  const userId       = localStorage.getItem("user_id") || "";
  const [items, setItems]                   = useState([]);
  const [returnQuantities, setReturnQuantities] = useState({});
  const [loading, setLoading]               = useState(true);
  const [isSubmitting, setIsSubmitting]     = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchRequisition = async () => {
      try {
        const res = await axios.get(`/requisitions/return/${id}`);
        const fetched = (res.data.items || []).map((item) => ({
          ...item,
          already_returned: item.already_returned || 0,
        }));
        setItems(fetched);
        const init = {};
        fetched.forEach((i) => { init[i.item_id] = 0; });
        setReturnQuantities(init);
        setLoading(false);
      } catch (err) {
        console.error("Error loading return data:", err);
        showToast("error", "Load Failed", "Could not load requisition data.");
        setLoading(false);
      }
    };
    fetchRequisition();
  }, [id]);

  const handleQuantityChange = (itemId, value) => {
    if (value < 0) return;
    const item = items.find((i) => i.item_id === itemId);
    if (!item) return;
    if (value + (item.already_returned || 0) > item.issued_quantity) {
      showToast("warning", "Exceeds Limit", `Cannot return more than issued quantity (${item.issued_quantity}).`);
      return;
    }
    setReturnQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = async () => {
    const hasReturn = Object.values(returnQuantities).some((q) => q > 0);
    if (!hasReturn) {
      showToast("warning", "Nothing to Return", "Please enter a return quantity for at least one item.");
      return;
    }
    for (const item of items) {
      const returnQty      = returnQuantities[item.item_id] || 0;
      const alreadyReturned = item.returned_quantity || 0;
      const maxAllowed     = item.issued_quantity - alreadyReturned;
      if (returnQty > maxAllowed) {
        showToast("warning", "Exceeds Limit", `Max returnable for "${item.item_name}" is ${maxAllowed}.`);
        return;
      }
    }

    const returnedItems = items
      .filter((i) => returnQuantities[i.item_id] > 0)
      .map((i) => ({ item_id: i.item_id, return_qty: returnQuantities[i.item_id] }));

    setIsSubmitting(true);
    try {
      await axios.post(`/requisitions/return/${id}`, { returnedItems, user_id: userId });
      showToast("success", "Return Processed", "Items returned to stock successfully.");
      navigate("/dashboard/requisition-list");
    } catch (err) {
      console.error("Error submitting return:", err);
      showToast("error", "Failed", "Could not process return. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div>
        <div className="ims-page-header">
          <div><h4>Return Items — #{id}</h4><p>Return items to stock</p></div>
          <button className="btn btn-sm btn-secondary" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left mr-1"></i> Back
          </button>
        </div>
        <div className="alert alert-info">No items available for return.</div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Return Items — #{id}</h4>
          <p>Enter quantities to return to stock</p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/requisition-list")}>
          <i className="fas fa-arrow-left mr-1"></i> Back
        </button>
      </div>

      {/* ── Items table ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
          <strong style={{ fontSize: "14px" }}>
            <i className="fas fa-undo mr-2 text-warning"></i>Items for Return
          </strong>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="ims-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Issued Qty</th>
                  <th>Already Returned</th>
                  <th>Return Qty</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const maxReturn  = item.issued_quantity - (item.returned_quantity || 0);
                  const fullyDone  = maxReturn <= 0;
                  return (
                    <tr key={item.item_id}>
                      <td><strong>{item.item_name}</strong></td>
                      <td>{item.issued_quantity}</td>
                      <td>{item.returned_quantity || 0}</td>
                      <td style={{ width: "140px" }}>
                        {fullyDone ? (
                          <span className="text-muted" style={{ fontSize: "12px" }}>Fully returned</span>
                        ) : (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: "90px" }}
                            min="0"
                            max={maxReturn}
                            value={returnQuantities[item.item_id] || 0}
                            onChange={(e) => handleQuantityChange(item.item_id, parseInt(e.target.value, 10) || 0)}
                            disabled={isSubmitting}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer" style={{ borderTop: "1px solid #f1f5f9", background: "#fff", borderRadius: "0 0 12px 12px" }}>
          <button
            className="btn btn-success btn-sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? <><i className="fas fa-spinner fa-spin mr-1"></i> Processing...</>
              : <><i className="fas fa-undo mr-1"></i> Submit Return</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnRequisitionForm;
